import type { IPeerConnection } from '../types';
import type { VideoSenderSuspension } from '../VideoSenderSuspension';
import type { NativeVideoCodec, NativeVideoMessage, NativeVideoSource } from './NativeVideoProtocol';
import type { DirectStreamDriver, NativeBootstrapBudget } from './stream/StreamSourceSelector';
import type { NativeVideoRecoveryLadder, WebCodecsFallbackReason } from './NativeVideoRecoveryLadder';
import type { NativeVideoRejectReason } from './NativeVideoReject';
import type { SenderClockAnchor } from './SenderClockAnchor';
import type { NativeEncodeDebug, NativeRtpTelemetry } from './NativeEncodeDebug';

export interface NativeHubPacket {
  header: { target: string; action: string; source: string; isReply: boolean };
  body: { message: Record<string, any> };
}
export type NativeVideoRejectPayload = { sessionId?: string; reason: NativeVideoRejectReason | string };

export interface NativeVideoTransport {
  send(packet: NativeHubPacket): void;
  subscribe(callback: (packet: NativeHubPacket) => void): () => void;
  subscribeRejects?(callback: (payload: NativeVideoRejectPayload) => void): () => void;
}
export type VideoRecoveryState = 'idle' | 'keyframe' | 'reset' | 'restart';
export type VideoStreamingStatus = {
  viewerId: string;
  active: 'electron' | 'native';
  pending: boolean;
  playback?: 'rtp' | 'webcodecs';
  requestedCodec?: NativeVideoCodec;
  codec?: string;
  reason?: string;
  sessionRestarts: number;
  recoveryState: VideoRecoveryState;
  fallbackReason?: WebCodecsFallbackReason;
};
export type NativeVideoTimer = ReturnType<typeof setTimeout> | undefined;
export interface NativeVideoClock {
  now(): number;
  setTimeout(handler: () => void, timeout: number): ReturnType<typeof setTimeout>;
  clearTimeout(timer: NativeVideoTimer): void;
}
export const systemClock: NativeVideoClock = {
  now: () => performance.now(),
  setTimeout: (handler, timeout) => setTimeout(handler, timeout),
  clearTimeout: (timer) => { if (timer !== undefined) clearTimeout(timer); },
};
export type NativeVideoViewer = {
  status: VideoStreamingStatus;
  peer: IPeerConnection;
  camera: string;
  session: string;
  hello: boolean;
  codecs?: NativeVideoCodec[];
  codec?: NativeVideoCodec;
  playback?: 'rtp' | 'webcodecs';
  configuredEpoch?: number;
  pendingReady?: NativeVideoMessage;
  offered: boolean;
  closed: boolean;
  timer?: NativeVideoTimer;
  disconnectTimer?: NativeVideoTimer;
  retryTimer?: NativeVideoTimer;
  phaseTimer?: NativeVideoTimer;
  damageTimer?: NativeVideoTimer;
  healthyTimer?: NativeVideoTimer;
  retries: number;
  sessionRestarts: number;
  recoveryState: VideoRecoveryState;
  fallbackReason?: WebCodecsFallbackReason;
  seenNative: boolean;
  restartQueued: boolean;
  restartToken: number;
  stallSinceMs?: number;
  keyframeAtMs?: number;
  diagnosticTime?: number;
  diagnosticSequence?: number;
  diagnosticsEnabled?: boolean;
  video: VideoSenderSuspension;
  bootstrap: NativeBootstrapBudget;
  ladder: NativeVideoRecoveryLadder;
  chain: Promise<void>;
  onData: (...args: unknown[]) => void;
  source: NativeVideoSource; signalingLabel: string; prefersStream: boolean;
  direct?: DirectStreamDriver;
  streamId: number;
  /** WebCodecs sender clock → host wall clock, from the sender's telemetry; cleared with the session. */
  senderClock: SenderClockAnchor;
  /** Latest native sender report for the host debug overlay. Cleared with the session. */
  encodeDebug?: NativeEncodeDebug;
  encodeDebugAtMs?: number;
  rtpTelemetry?: NativeRtpTelemetry;
};
